import { format } from "date-fns";
import { ArrowLeft, Calendar, Eye, EyeOff } from "lucide-react";
import PropTypes from "prop-types";
import React, { useState } from "react";
import { Card, Row, Col, Badge, Button } from "react-bootstrap";
import { Link } from "react-router-dom";
import ProgramActions from "./ProgramActions";
import ProgramOverview from "./ProgramOverview";

const TherapistProgramHeader = ({
	selectedDate,
	program,
	onProgramDelete,
	onDateChange,
	onPreviewClient,
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  const formattedDate = format(new Date(selectedDate), "EEEE, d MMMM yyyy");
  const getDayLabel = (day) => {
    const dayMap = {
      MON: "M",
      TUE: "Tu",
      WED: "W",
      THU: "Th",
      FRI: "F",
      SAT: "Sa",
      SUN: "Su",
    };
    return dayMap[day] || day;
  };

  const toggleFeedback = () => {
    setShowFeedback(!showFeedback);
  };

  return (
    <>
      <Card className="bg-gradient-to-r from-pink-50 to-white shadow-sm mb-4">
        <Card.Body className="p-4">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center">
              <h2 className="text-2xl font-semibold text-gray-800 ml-2">
                <Link
                  className="text-primary hover:text-pink-600 transition-colors flex items-center me-3"
                  to={`/client/${program.client.user.id}`}
                >
                  <ArrowLeft size={24} />
                </Link>
                {program.client.is_practice ? "Julia Smith" : program.client_name}
              </h2>
            </div>
            <div className="flex items-center gap-2 bg-white py-2 rounded-full shadow-sm">
              <Calendar size={18} className="text-pink-500 me-2" />
              <span className="text-gray-700 font-medium">{formattedDate}</span>
            </div>
          </div>
        </Card.Body>
      </Card>
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-start justify-content-between mb-4">
            <Col>
              <h3 className="mb-0">{program.name}</h3>
            </Col>
            <Col xs="auto" className="d-flex align-items-center gap-2">
              {onPreviewClient && program.client.is_practice && (
                <Button
                  type="button"
                  variant="outline-primary"
                  size="sm"
                  className="d-inline-flex align-items-center"
                  onClick={onPreviewClient}
                  data-walkthrough="preview"
                >
                  <Eye size={16} className="me-2" />
                  Preview client view
                </Button>
              )}
              <ProgramActions program={program} onProgramDelete={onProgramDelete}
                onPreviewClient={onPreviewClient} showPreviewInMenu={!program.client.is_practice} />
            </Col>
          </Row>

          <Row>
            <Col md={4}>
              <div className="mb-3">
                <h6 className="text-muted">Start Date</h6>
                <p className="text-dark">
                  {format(new Date(program.start_date), "d MMMM, yyyy")}
                </p>
              </div>
            </Col>
            <Col md={4}>
              <div className="mb-3">
                <h6 className="text-muted">End Date</h6>
                <p className="text-dark">
                  {format(new Date(program.end_date), "d MMMM, yyyy")}
                </p>
              </div>
            </Col>

            <Col xs={12} md={8}>
              <div className="mb-3">
                <h6 className="text-muted">Schedule</h6>
                <div className="d-flex flex-wrap gap-1 align-items-center">
                  {program.days_of_week.map((day) => (
                    <Badge 
                      key={day} 
                      bg="custom-pink" 
                      className="me-1 d-inline-flex align-items-center justify-content-center"
                      style={{ width: '32px', height: '32px', borderRadius: '50%', padding: 0 }}
                    >
                      {getDayLabel(day)}
                    </Badge>
                  ))}
                </div>
              </div>
            </Col>
            <Col xs={12} md={4} className="mb-3">
              <Button
                variant="primary"
                className="d-inline-flex align-items-center text-white"
                onClick={toggleFeedback}
              >
                {showFeedback ? <EyeOff size={16} className="me-2" /> : <Eye size={16} className="me-2" />}
                {showFeedback ? 'Hide Days Completed' : 'Show Days Completed'}
              </Button>
            </Col>

            {program.is_draft && (
              <Col
                md={4}
                className="d-flex justify-content-end align-items-end my-2"
              >
                <Badge bg="secondary" className="px-3 py-2 text-light">
                  Draft
                </Badge>
              </Col>
            )}
          </Row>
        </Card.Body>
      </Card>
      {showFeedback && (
        <div id="program-overview" className="my-4">
          <ProgramOverview
            program={program}
            selectedDate={selectedDate}
            onDateChange={onDateChange}
          />
        </div>
      )}
    </>
  );
};

TherapistProgramHeader.propTypes = {
  selectedDate: PropTypes.string.isRequired,
  program: PropTypes.shape({
    client: PropTypes.shape({
      is_practice: PropTypes.bool,
      user: PropTypes.shape({
        id: PropTypes.number.isRequired,
      }).isRequired,
    }).isRequired,
    client_name: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    start_date: PropTypes.string.isRequired,
    end_date: PropTypes.string.isRequired,
    days_of_week: PropTypes.arrayOf(PropTypes.string).isRequired,
    is_draft: PropTypes.bool,
  }).isRequired,
  onProgramDelete: PropTypes.func,
  onDateChange: PropTypes.func.isRequired,
  onPreviewClient: PropTypes.func,
};

export default TherapistProgramHeader;